import { ShoppingBag01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/shared/ui/Button'

import { StoreNudgeHighlight } from './StoreNudgeHighlight'
import { StoreNudgeThumbStrip } from './StoreNudgeThumbStrip'
import { itemImageUrl } from '../lib/itemImageUrl'
import { STORE_NUDGE_COPY, STORE_NUDGE_HERO } from '../model/constants'


import type { StoreNudgeCatalogItem } from '../lib/isRedeemableNow'
import type { StoreNudgeBatch } from '../model/types'

export type StoreNudgeDialogProps = {
  open: boolean
  batch: StoreNudgeBatch | null
  reducedMotion?: boolean
  onDismiss: () => void
  onGoToStore: () => void
}

const HERO_ENTER_CLASS = 'animate-in fade-in zoom-in-95 duration-500'

// O `DialogContent` do shadcn ocupa a tela inteira no mobile. Um aviso de
// conquista é um momento, não uma tela: vira card centralizado com margem em
// qualquer largura, com a ilustração em evidência.
const CONTENT_CLASS =
  'inset-4 m-auto h-fit max-h-[calc(100dvh-2rem)] w-auto max-w-[min(100%-2rem,26rem)] overflow-y-auto rounded-2xl border p-5 sm:p-6'

const subtitleFor = (batch: StoreNudgeBatch) => {
  if (batch.kind === 'afford') return STORE_NUDGE_COPY.afford.subtitle
  return batch.unlocked.length === 1
    ? STORE_NUDGE_COPY.unlocked.subtitleOne
    : STORE_NUDGE_COPY.unlocked.subtitleMany(batch.unlocked.length)
}

/**
 * O popup do nudge da lojinha: um modal centralizado só, com dois conteúdos
 * (moedas ou desbloqueio). Puro: recebe o lote pronto e devolve intenções.
 */
export const StoreNudgeDialog = ({
  open,
  batch,
  reducedMotion = false,
  onDismiss,
  onGoToStore
}: StoreNudgeDialogProps) => {
  if (!batch) return null

  const title = STORE_NUDGE_COPY[batch.kind].title
  const hero = STORE_NUDGE_HERO[batch.kind]
  const showAffordSecondary = batch.kind === 'unlocked' && batch.affordable !== null
  const thumbs = batch.thumbs.map(item => ({
    id: item.id,
    name: item.name,
    imageUrl: itemImageUrl(item as StoreNudgeCatalogItem)
  }))

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onDismiss()}>
      <DialogContent className={CONTENT_CLASS}>
        <div className="flex flex-col items-center gap-4">
          <picture className="contents">
            <source type="image/avif" srcSet={hero.avifSrcSet} />
            <img
              src={hero.src}
              srcSet={hero.webpSrcSet}
              width={175}
              height={144}
              alt=""
              aria-hidden
              decoding="async"
              data-testid="store-nudge-hero"
              className={clsx('h-36 w-auto max-w-full', !reducedMotion && HERO_ENTER_CLASS)}
            />
          </picture>

          <div className="flex flex-col gap-1 text-center">
            <DialogTitle className="m-0 text-xl font-bold leading-tight text-gray-900">
              {title}
            </DialogTitle>
            <DialogDescription className="m-0 text-sm text-gray-700">
              {subtitleFor(batch)}
            </DialogDescription>
          </div>

          {batch.kind === 'afford' && batch.affordable && (
            <StoreNudgeHighlight
              name={batch.affordable.item.name}
              imageUrl={itemImageUrl(batch.affordable.item as StoreNudgeCatalogItem)}
              price={batch.affordable.item.coin}
              onSelect={onGoToStore}
            />
          )}

          {batch.kind === 'unlocked' && (
            <div
              className={clsx(
                'flex w-full gap-3',
                batch.unlocked.length > 1 ? 'overflow-x-auto pb-1' : 'flex-col'
              )}
            >
              {batch.unlocked.map(entry => (
                <StoreNudgeHighlight
                  key={entry.reward.id}
                  name={entry.highlightItem.name}
                  imageUrl={itemImageUrl(entry.highlightItem as StoreNudgeCatalogItem)}
                  price={entry.affordable ? entry.highlightItem.coin : null}
                  unlocked
                  onSelect={onGoToStore}
                  className={batch.unlocked.length > 1 ? 'w-64 shrink-0' : undefined}
                />
              ))}
            </div>
          )}

          {showAffordSecondary && batch.affordable && (
            <p className="m-0 text-center text-sm font-semibold text-gray-800">
              {STORE_NUDGE_COPY.affordSecondary(batch.affordable.item.name)}
            </p>
          )}

          <StoreNudgeThumbStrip
            thumbs={thumbs}
            overflow={batch.thumbsOverflow}
            othersCount={batch.othersCount}
            onSelect={onGoToStore}
          />

          <div className="flex w-full flex-col gap-2 pt-1">
            <Button
              variant="primary"
              className="w-full"
              leadingIcon={<ShoppingBag01Icon size={18} aria-hidden />}
              showTrailingIcon={false}
              onClick={onGoToStore}
            >
              {STORE_NUDGE_COPY.primaryAction}
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              showTrailingIcon={false}
              onClick={onDismiss}
            >
              {STORE_NUDGE_COPY.secondaryAction}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

StoreNudgeDialog.displayName = 'StoreNudgeDialog'
